import Modal from '../../../../components/modal/modalComponent';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecommendation } from './useRecommendation';
import Loader from '../../../../components/loaderComponent';
import { RecommendationItem } from './RecommendationItem';
import { ServiceRequestStatus } from '../../../../graphql/generated/hasura';
import PaginateBlock from '../../../../components/PaginateBlock';
import { ModalButtonProps } from '../../../../components/modal/modal.interfaces';
import { genericActionsIds } from '../../../../utilities/constants';
import { useGetGenericActions } from '../../../../hooks/useGetGenericActions';

interface IArchiveRecommendationModal {
  locale: Record<string, string>;
  addRecommendationToCart: (
    productId: number,
    quantity: number,
    recommendationId: string,
  ) => Promise<unknown>;
}

export interface IGetArchiveRecommendationFilter {
  statuses: ServiceRequestStatus[];
  offset: number;
  limit: number;
}

const initialFilter = {
  limit: 5,
  offset: 0,
  statuses: [ServiceRequestStatus.OnHold],
};

export const ArchiveRecommendationModal: React.FC<
  IArchiveRecommendationModal
> = ({ locale, addRecommendationToCart }) => {
  const navigate = useNavigate();
  const { data: genericAction } = useGetGenericActions({
    locale: 'en',
    genericActionId: [genericActionsIds.CLOSE],
  });

  const [filter, setFilter] =
    useState<IGetArchiveRecommendationFilter>(initialFilter);

  const {
    data,
    loading: dataLoading,
    total,
    handleDeleteRecommendation,
  } = useRecommendation(filter);

  const handleClose = () => {
    navigate(location.pathname, { replace: true });
  };

  const Buttons: ModalButtonProps[] = [
    {
      label: genericAction?.[genericActionsIds.CLOSE]?.close,
      disabled: false,
      onClick: handleClose,
      testID: 'close',
    },
  ];

  const hasShowPaginate = useMemo(
    () => !!total && total > filter.limit,
    [total, filter.limit],
  );

  const paginateSubtitle = useMemo(
    () =>
      `${locale?.showing} ${filter.offset + 1}-${Math.min(
        filter.offset + filter.limit,
        total || 0,
      )} ${locale?.testKit} ${locale?.from} ${total}`,
    [locale, total, filter],
  );

  const setNewOffset = (offset: number) =>
    setFilter((vars) => ({ ...vars, offset }));

  return (
    <Modal
      isOpen={true}
      onClose={handleClose}
      buttons={Buttons}
      title={locale.archiveTitle}
      contentContainerDefaultBackground="bg-base-content"
      modalDesktopWidth="desktop:max-w-[1100px]"
    >
      {dataLoading ? (
        <div data-testid="upcoming-appointments-loading">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col gap-2.5">
          <div className="w-full h-full flex flex-col gap-2.5">
            {data?.length ? (
              data?.map((recommendation) => (
                <RecommendationItem
                  key={recommendation.id}
                  recommendation={recommendation}
                  locale={locale}
                  isArchive
                  deleteRecommendation={() =>
                    handleDeleteRecommendation(recommendation.id)
                  }
                  addRecommendationToCart={addRecommendationToCart}
                />
              ))
            ) : (
              <div className="flex h-full items-center justify-center text-lg font-semibold text-btn-black">
                {locale?.noArchivedRecommendations}
              </div>
            )}
          </div>

          {hasShowPaginate && !dataLoading && (
            <div className="flex w-full max-desktop:flex-col gap-4 justify-between items-center">
              <p className="text-sm font-medium text-med-gray-3">
                {paginateSubtitle}
              </p>
              <PaginateBlock
                itemsPerPage={filter.limit}
                totalItems={total || 0}
                setNewOffset={setNewOffset}
                customOffset={filter.offset || 0}
              />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};
